import { getLanguage as getInterfaceLanguage } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState } from 'react';
import { ComparedVideo, DuelPitch, formatTime, VIDEO_COLORS, RetentionData, RetentionPoint } from './videoDuelData';
import { Plus, Play, RefreshCw, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { VideoComparisonModal } from './VideoComparisonModal';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip, ReferenceLine } from 'recharts';
import { DateRange } from 'react-day-picker';
import { PeriodPicker } from '@/components/ui/period-picker';
import { pitchLabel } from '@/utils/pitches';

interface VideoSelectorBlockProps {
  videoA: string | null;
  videoB: string | null;
  videoC: string | null;
  onSelectA: (id: string | null) => void;
  onSelectB: (id: string | null) => void;
  onSelectC: (id: string | null) => void;
  videos: ComparedVideo[];
  retention?: RetentionData | null;
  dateRange?: DateRange;
  onDateRangeChange?: (range: DateRange | undefined) => void;
}

interface VideoCardProps {
  label: string;
  video: ComparedVideo | null;
  color: string;
  onClickChange: () => void;
  onRemove?: () => void;
  isRemovable?: boolean;
  retentionData?: RetentionPoint[];
  /** Pitches do vídeo: uma linha no gráfico e um chip com a retenção de cada um. */
  pitches?: DuelPitch[];
  biggestDrop?: number;
  disabled?: boolean;
}

function VideoCard({
  label,
  video,
  color,
  onClickChange,
  onRemove,
  isRemovable,
  retentionData,
  pitches = [],
  biggestDrop,
  disabled,
}: VideoCardProps) {
  useLanguage();
  if (!video) {
    return (
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-2.5" style={{ minHeight: 20 }}>
          <div className="flex items-center gap-2">
            <div className="h-2 w-2 rounded-full" style={{ backgroundColor: color }} />
            <span className="text-xs font-semibold text-[hsl(var(--muted-foreground))] uppercase tracking-wide">{label}</span>
          </div>
        </div>
        <div
          className="bg-[hsl(var(--card))] rounded-2xl border border-[hsl(var(--border))] overflow-hidden"
          style={{ aspectRatio: '16/9' }}
        >
          <button
            onClick={disabled ? undefined : onClickChange}
            disabled={disabled}
            className={`w-full h-full group relative overflow-hidden transition-colors duration-200 ${
              disabled ? 'bg-[#F9FAFB] opacity-50 cursor-not-allowed' : 'bg-[#FAFBFC] hover:bg-[#F1F5F9] cursor-pointer'
            }`}
          >
            <div
              className={`absolute inset-0 flex flex-col items-center justify-center gap-3 border border-dashed rounded-2xl ${
                disabled ? 'border-[#E5E7EB]' : 'border-[#D1D5DB] group-hover:border-[#94A3B8]'
              }`}
            >
              <div
                className={`h-11 w-11 rounded-full border flex items-center justify-center transition-colors duration-200 ${
                  disabled
                    ? 'bg-[#F3F4F6] border-[#E5E7EB]'
                    : 'bg-[#F1F5F9] border-[#E2E8F0] group-hover:bg-[#E0EAFF] group-hover:border-[#93B4FD]'
                }`}
              >
                <Plus
                  className={`h-5 w-5 transition-colors duration-200 ${
                    disabled ? 'text-[#D1D5DB]' : 'text-[#94A3B8] group-hover:text-[#4B7BFF]'
                  }`}
                />
              </div>
              <p
                className={`text-[12px] transition-colors duration-200 ${
                  disabled ? 'text-[#D1D5DB]' : 'text-[#9CA3AF] group-hover:text-[#6B7280]'
                }`}
              >
                {translateInterfaceText("Selecionar vídeo")}</p>
            </div>
          </button>
        </div>
      </div>
    );
  }

  const CustomTooltip = ({ active, payload }: any) => {
  useLanguage();
    if (!active || !payload?.length) return null;
    const data = payload[0]?.payload;
    if (!data) return null;
    return (
      <div
        style={{
          fontSize: 12,
          fontWeight: 500,
          borderRadius: 8,
          border: '1px solid #E5E7EB',
          backgroundColor: '#FFFFFF',
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
          padding: '10px 14px',
          lineHeight: '1.7',
          pointerEvents: 'none',
          minWidth: 160,
        }}
      >
        <div style={{ color: '#6B7280', fontSize: 11, fontWeight: 500, marginBottom: 4 }}>
          {translateInterfaceText("TEMPO:")}{" "}{formatTime(data.second)}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
          <div style={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: color, flexShrink: 0 }} />
          <span style={{ color: '#111827', fontWeight: 600, fontSize: 13 }}>
            {translateInterfaceText("Retenção no momento")}{'  '}
            {data.retention.toFixed(1)}%
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
          <div style={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: '#3B82F6', flexShrink: 0 }} />
          <span style={{ color: '#374151', fontWeight: 500, fontSize: 12 }}>
            {translateInterfaceText("Visualizações")}{'  '}
            {data.views.toLocaleString(getInterfaceLanguage())}
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className="flex-1 min-w-0 group">
      <div className="flex items-center justify-between mb-2.5" style={{ minHeight: 20 }}>
        <div className="flex items-center gap-2">
          <div className="h-2 w-2 rounded-full" style={{ backgroundColor: color }} />
          <span className="text-xs font-semibold text-[hsl(var(--muted-foreground))] uppercase tracking-wide">{label}</span>
        </div>
        <button
          onClick={onClickChange}
          className="opacity-0 group-hover:opacity-100 flex items-center gap-1.5 text-[11px] font-medium text-[hsl(var(--primary))] hover:text-[hsl(var(--primary))]/80 transition-opacity"
        >
          <RefreshCw className="h-3 w-3" />
          {translateInterfaceText("Trocar")}</button>
      </div>
      <div className="bg-[hsl(var(--card))] rounded-2xl border border-[hsl(var(--border))] overflow-hidden relative">
        {isRemovable && (
          <button
            onClick={onRemove}
            className="absolute top-2 right-2 z-10 h-7 w-7 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center hover:bg-black/80 transition-colors"
          >
            <X className="h-3.5 w-3.5 text-white" />
          </button>
        )}

        {retentionData && retentionData.length > 0 ? (
          <div className="relative w-full" style={{ aspectRatio: '16/9' }}>
            {video.thumbnailUrl && (
              <img src={video.thumbnailUrl} alt={video.name} className="absolute inset-0 w-full h-full object-cover" />
            )}
            <div className="absolute inset-0 bg-black/50" />
            <div className="absolute inset-0">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={retentionData} margin={{ top: 12, right: 12, left: 0, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                  <XAxis
                    dataKey="second"
                    tickFormatter={formatTime}
                    tick={{ fontSize: 9, fill: 'rgba(255,255,255,0.6)' }}
                    axisLine={{ stroke: 'rgba(255,255,255,0.15)' }}
                    tickLine={false}
                  />
                  <YAxis
                    domain={[0, 100]}
                    tick={{ fontSize: 9, fill: 'rgba(255,255,255,0.6)' }}
                    axisLine={{ stroke: 'rgba(255,255,255,0.15)' }}
                    tickFormatter={(v) => `${v}%`}
                    tickLine={false}
                    width={35}
                  />
                  <Tooltip
                    content={<CustomTooltip />}
                    cursor={{ stroke: 'rgba(107,114,128,0.5)', strokeWidth: 1, strokeDasharray: '4 4' }}
                  />
                  {pitches.map((pitch, index) => (
                    <ReferenceLine
                      key={pitch.id}
                      // A série do duelo vem em baldes de 5s (eixo categórico): a linha
                      // cai no balde do pitch, o mesmo usado no cálculo da retenção.
                      x={Math.floor(pitch.start / 5) * 5}
                      stroke={color}
                      strokeDasharray="6 4"
                      strokeWidth={1.5}
                      label={{ value: pitchLabel(pitch, index), fill: '#FFFFFF', fontSize: 9, fontWeight: 700, position: 'top', offset: 2 }}
                    />
                  ))}
                  {biggestDrop != null && biggestDrop > 0 && (
                    <ReferenceLine
                      x={biggestDrop}
                      stroke="#DC2626"
                      strokeDasharray="5 5"
                      label={{ value: 'Queda', fill: '#DC2626', fontSize: 9, position: 'top' }}
                    />
                  )}
                  <Line
                    type="monotone"
                    dataKey="retention"
                    stroke={color}
                    strokeWidth={2.5}
                    dot={false}
                    activeDot={{ r: 4, fill: color, stroke: '#fff', strokeWidth: 2 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        ) : (
          <div
            className="relative aspect-video bg-[hsl(var(--muted))] overflow-hidden cursor-pointer"
            onClick={onClickChange}
          >
            {video.thumbnailUrl ? (
              <img src={video.thumbnailUrl} alt={video.name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Play className="h-10 w-10 text-[hsl(var(--muted-foreground))/0.3]" />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Chips: retenção em cada pitch do vídeo */}
      {pitches.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {pitches.map((pitch, index) => (
            <span
              key={pitch.id}
              className="inline-flex items-center gap-1.5 rounded-full border border-[#E5E7EB] bg-white px-2 py-0.5 text-[11px] font-medium text-[#374151]"
            >
              <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: color }} />
              {pitchLabel(pitch, index)} · {pitch.retention != null ? `${pitch.retention.toFixed(1)}%` : '—'}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export function VideoSelectorBlock({
  videoA,
  videoB,
  videoC,
  onSelectA,
  onSelectB,
  onSelectC,
  videos,
  retention,
  dateRange,
  onDateRangeChange,
}: VideoSelectorBlockProps) {
  useLanguage();
  const [modalOpen, setModalOpen] = useState(false);
  const [modalSlot, setModalSlot] = useState<'A' | 'B' | 'C'>('A');

  const selectedA = videos.find((v) => v.id === videoA) || null;
  const selectedB = videos.find((v) => v.id === videoB) || null;
  const selectedC = videos.find((v) => v.id === videoC) || null;

  const openModal = (slot: 'A' | 'B' | 'C') => {
    setModalSlot(slot);
    setModalOpen(true);
  };

  const handleModalSelect = (id: string) => {
    const scrollY = window.scrollY;
    if (modalSlot === 'A') onSelectA(id);
    else if (modalSlot === 'B') onSelectB(id);
    else onSelectC(id);
    setModalOpen(false);
    requestAnimationFrame(() => {
      window.scrollTo({ top: scrollY, behavior: 'instant' as ScrollBehavior });
    });
  };

  const selectedIds = [videoA, videoB, videoC].filter(Boolean) as string[];

  return (
    <>
      <div className="flex justify-end">
        <PeriodPicker
          value={dateRange}
          onChange={(range) => onDateRangeChange?.(range)}
          align="end"
          className="h-9 min-h-0 w-auto text-xs"
        />
      </div>

      <div className="bg-white rounded-xl border border-[#E6EAF2] p-6 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
        <div className="flex flex-col md:flex-row items-stretch md:items-start gap-4">
          <VideoCard
            label={translateInterfaceText("Vídeo A")}
            video={selectedA}
            color={VIDEO_COLORS[0]}
            onClickChange={() => openModal('A')}
            retentionData={retention?.videoA}
            pitches={retention?.pitchesA}
            biggestDrop={retention?.biggestDropA}
          />

          <VideoCard
            label={translateInterfaceText("Vídeo B")}
            video={selectedB}
            color={VIDEO_COLORS[1]}
            onClickChange={() => openModal('B')}
            disabled={!videoA}
            retentionData={retention?.videoB}
            pitches={retention?.pitchesB}
            biggestDrop={retention?.biggestDropB}
          />

          <VideoCard
            label={translateInterfaceText("Vídeo C")}
            video={selectedC}
            color={VIDEO_COLORS[2]}
            onClickChange={() => openModal('C')}
            disabled={!videoA || !videoB}
            onRemove={() => onSelectC(null)}
            isRemovable={!!videoC}
            retentionData={retention?.videoC}
            pitches={retention?.pitchesC}
            biggestDrop={retention?.biggestDropC}
          />
        </div>
      </div>

      <VideoComparisonModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        videos={videos}
        selectedIds={selectedIds}
        excludeId={modalSlot === 'A' ? videoA : modalSlot === 'B' ? videoB : videoC}
        onSelect={handleModalSelect}
        slot={modalSlot}
      />
    </>
  );
}
